import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAuth } from "../useAuth";
import axiosInstance from "@/lib/axios";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { useDateRangeState } from "@lib/hooks/url-state";
import { useTimezoneAwareDates } from "@lib/hooks/use-most-ahead-timezone";
import dayjs from "dayjs";
import type {
  DiscountCodeAllItem,
  DiscountCodeAllTotals,
  DiscountCodesAllResponse,
  UseDiscountCodesAllParams,
} from "./types";

async function fetchDiscountCodesForSite(
  siteId: string,
  params: {
    start_date: string;
    end_date: string;
    timezone?: string;
    limit?: number;
    offset?: number;
    new_vs_returning?: string;
  },
  token: string
): Promise<DiscountCodesAllResponse> {
  const searchParams = new URLSearchParams();
  searchParams.append("start_date", params.start_date);
  searchParams.append("end_date", params.end_date);
  if (params.timezone) {
    searchParams.append("timezone", params.timezone);
  }
  if (params.limit !== undefined) {
    searchParams.append("limit", params.limit.toString());
  }
  if (params.offset !== undefined) {
    searchParams.append("offset", params.offset.toString());
  }
  if (params.new_vs_returning) {
    searchParams.append("new_vs_returning", params.new_vs_returning);
  }

  const url = `${process.env.NEXT_PUBLIC_TRACIFY_API_BASE_URL}/analytics/events/${siteId}/discount_codes?${searchParams.toString()}`;

  const response = await axiosInstance.get<DiscountCodesAllResponse>(url);

  if (response.status !== 200) {
    throw new Error(`Failed to fetch discount codes: ${response.statusText}`);
  }

  const data = response.data;

  return {
    ...data,
    items: (data.items ?? []).map((item) => ({
      discount_code: item.discount_code,
      count: item.count ?? 0,
      revenue: item.revenue ?? 0,
      returning_customer_rate: item.returning_customer_rate ?? 0,
      new_customer_rate: item.new_customer_rate ?? 0,
    })),
    totalItemCount: data.count ?? 0,
    totals: data.totals,
  };
}

export async function fetchDiscountCodesAll(
  params: UseDiscountCodesAllParams,
  token: string
): Promise<DiscountCodesAllResponse> {
  if (params.siteIds.length === 0) {
    return { items: [], count: 0, totalItemCount: 0 };
  }

  const baseParams = {
    start_date: params.start_date,
    end_date: params.end_date,
    timezone: params.timezone,
    limit: params.limit ?? 1000,
    offset: params.offset ?? 0,
    new_vs_returning: params.new_vs_returning,
  };

  if (params.siteIds.length === 1) {
    const result = await fetchDiscountCodesForSite(
      params.siteIds[0],
      baseParams,
      token
    );
    return { ...result, totalItemCount: result.count, totals: result.totals };
  }

  const responses = await Promise.all(
    params.siteIds.map((siteId) =>
      fetchDiscountCodesForSite(siteId, baseParams, token)
    )
  );

  type Agg = {
    count: number;
    revenue: number;
    returningWeightedSum: number;
    newWeightedSum: number;
  };
  const aggregated = new Map<string, Agg>();
  let totalCount = 0;
  const aggregatedTotals: DiscountCodeAllTotals = { order: 0, revenue: 0 };

  for (const response of responses) {
    totalCount += response.count;
    if (response.totals) {
      aggregatedTotals.order += response.totals.order;
      aggregatedTotals.revenue += response.totals.revenue;
    }
    for (const item of response.items) {
      const existing = aggregated.get(item.discount_code) ?? {
        count: 0,
        revenue: 0,
        returningWeightedSum: 0,
        newWeightedSum: 0,
      };
      const count = item.count ?? 0;
      aggregated.set(item.discount_code, {
        count: existing.count + count,
        revenue: existing.revenue + (item.revenue ?? 0),
        returningWeightedSum:
          existing.returningWeightedSum +
          count * (item.returning_customer_rate ?? 0),
        newWeightedSum:
          existing.newWeightedSum + count * (item.new_customer_rate ?? 0),
      });
    }
  }

  const items: DiscountCodeAllItem[] = Array.from(aggregated.entries())
    .map(([discount_code, agg]) => ({
      discount_code,
      count: agg.count,
      revenue: agg.revenue,
      returning_customer_rate:
        agg.count > 0 ? agg.returningWeightedSum / agg.count : 0,
      new_customer_rate: agg.count > 0 ? agg.newWeightedSum / agg.count : 0,
    }))
    .sort((a, b) => b.count - a.count);

  return {
    items,
    count: totalCount,
    totalItemCount: items.length,
    totals: aggregatedTotals,
  };
}

export interface UseDiscountCodesAllProps {
  enabled?: boolean;
  limit?: number;
  offset?: number;
  /** When set, fetch for these site ids only (e.g. single store for subtab). Otherwise uses selected stores from context. */
  siteIds?: string[];
  newVsReturning?: string | null;
}

export function useDiscountCodesAll(props: UseDiscountCodesAllProps = {}) {
  const {
    enabled = true,
    limit = 100,
    offset = 0,
    siteIds: siteIdsOverride,
    newVsReturning,
  } = props;
  const { data: authData } = useAuth();
  const token = authData?.token;
  const { selectedStoreIds } = useSelectedStores();
  const stableStoreIds = useMemo(
    () => [...(siteIdsOverride ?? selectedStoreIds)].sort(),
    [siteIdsOverride, selectedStoreIds]
  );
  const { startTime, endTime } = useDateRangeState();
  const { timezone } = useTimezoneAwareDates();

  const startDateTime = dayjs(startTime).format("YYYY-MM-DD 00:00:00");
  const endDateTime = dayjs(endTime).format("YYYY-MM-DD 23:59:59");

  return useQuery({
    queryKey: [
      "discount-codes-all",
      newVsReturning,
      stableStoreIds,
      startDateTime,
      endDateTime,
      timezone,
      limit,
      offset,
    ],
    queryFn: () =>
      fetchDiscountCodesAll(
        {
          siteIds: stableStoreIds,
          start_date: startDateTime,
          end_date: endDateTime,
          timezone: timezone ?? undefined,
          limit,
          offset,
          new_vs_returning: newVsReturning ?? undefined,
        },
        token as string
      ),
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!token && enabled && stableStoreIds.length > 0,
  });
}
